import { apiClient } from '@/shared/lib/apiClient';
import type { ApiResponse, Destination, LiveCity, PaginatedResponse } from '@/types';

export interface AdminDestinationsParams {
  page?: number;
  limit?: number;
  search?: string;
  isLive?: 'true' | 'false' | 'all';
}

export interface DestinationPayload {
  name: string;
  region?: string;
  highlight?: string;
  imageUrl?: string;
  imagePublicId?: string;
  isLive?: boolean;
  sortOrder?: number;
}

export const destinationsApi = {
  async list(params?: { page?: number; limit?: number; country?: string; search?: string }) {
    const response = await apiClient.get<PaginatedResponse<Destination>>('/destinations', {
      params,
    });
    return response.data;
  },

  async listLive() {
    const response = await apiClient.get<ApiResponse<LiveCity[]>>('/destinations/live');
    return response.data.data ?? [];
  },

  async getById(id: string) {
    const response = await apiClient.get<ApiResponse<Destination>>(`/destinations/${id}`);
    return response.data.data!;
  },

  async adminList(params?: AdminDestinationsParams) {
    const response = await apiClient.get<PaginatedResponse<Destination>>('/destinations/admin', {
      params,
    });
    return response.data;
  },

  async create(payload: DestinationPayload) {
    const response = await apiClient.post<ApiResponse<Destination>>('/destinations', payload);
    return response.data.data!;
  },

  async update(id: string, payload: Partial<DestinationPayload>) {
    const response = await apiClient.patch<ApiResponse<Destination>>(`/destinations/${id}`, payload);
    return response.data.data!;
  },

  async uploadImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<ApiResponse<{ url: string; publicId: string }>>(
      '/upload/destination-image',
      formData
    );
    return response.data.data!;
  },
};

export const destinationsKeys = {
  all: ['destinations'] as const,
  list: () => [...destinationsKeys.all, 'list'] as const,
  live: () => [...destinationsKeys.all, 'live'] as const,
  admin: (params?: AdminDestinationsParams) =>
    [...destinationsKeys.all, 'admin', params ?? {}] as const,
  detail: (id: string) => [...destinationsKeys.all, 'detail', id] as const,
};

/** @deprecated Use destinationsApi from '@/features/destinations/api/destinations.api' */
export const destinationsService = destinationsApi;
